import type {
  Configuration,
  SurfaceTypesMapValue,
  SurfaceMaps,
  SurfaceOptionsGroup,
} from '~/types/configurator'

function getFormattedPrice(
  price: number | null | undefined,
  currency: Configuration['currency'],
  showZeroPrice: boolean = false,
  showSign: boolean = true
) {
  const formattedPrice = formatPrice(price, currency)

  if (formattedPrice === PRICE_EMPTY || (price === 0 && !showZeroPrice)) {
    return ''
  }

  if (!showSign) {
    return formattedPrice
  }

  return formattedPrice[0] === '-' ? `${formattedPrice}` : `+${formattedPrice}`
}

function findAccessory(
  accessoriesOptions?: Configuration['accessoriesOptions'],
  accessoryCategoryNid?: number | null,
  accessoryNid?: number | null
) {
  if (!accessoriesOptions || !accessoryCategoryNid || !accessoryNid) {
    return {}
  }

  for (const option of accessoriesOptions) {
    for (const accessory of option.values) {
      if (
        accessory.accessoryNid === accessoryNid &&
        option.partCategoryNid === accessoryCategoryNid
      ) {
        return { option, accessory }
      }
    }
  }

  return {}
}

function findSurfaceCategoryByNid(
  surfaceTypes: Map<number, SurfaceTypesMapValue>,
  surfaceMaps: SurfaceMaps,
  surfaceCategoryNid: number | null | undefined
) {
  if (surfaceCategoryNid === null || surfaceCategoryNid === undefined) {
    return {}
  }

  const { typeNid } =
    surfaceMaps.surfaceCategories.get(surfaceCategoryNid) || {}

  if (!typeNid) {
    return {}
  }

  const surfaceType = surfaceTypes.get(typeNid)
  const surfaceCategory = surfaceType?.surfaceCategories.get(surfaceCategoryNid)
  if (!surfaceCategory) {
    return {}
  }

  return {
    type: surfaceType,
    category: surfaceCategory,
  }
}

function findSurfaceCollectionByNid(
  surfaceTypes: Map<number, SurfaceTypesMapValue>,
  surfaceMaps: SurfaceMaps,
  surfaceCollectionNid: number | null | undefined
) {
  if (surfaceCollectionNid === null || surfaceCollectionNid === undefined) {
    return {}
  }

  const { typeNid, categoryNid } =
    surfaceMaps.surfaceCollections.get(surfaceCollectionNid) || {}

  if (!typeNid || !categoryNid) {
    return {}
  }

  const surfaceType = surfaceTypes.get(typeNid)
  const surfaceCategory = surfaceType?.surfaceCategories.get(categoryNid)
  const surfaceCollection =
    surfaceCategory?.surfaceCollections.get(surfaceCollectionNid)
  if (!surfaceCollection) {
    return {}
  }

  return {
    type: surfaceType,
    category: surfaceCategory,
    collection: surfaceCollection,
  }
}

function findSurfaceByNid(
  surfaceTypes: Map<number, SurfaceTypesMapValue>,
  surfaceMaps: SurfaceMaps,
  surfaceNid: number | null | undefined
) {
  if (surfaceNid === null || surfaceNid === undefined) {
    return {}
  }

  const { typeNid, categoryNid, collectionNid } =
    surfaceMaps.surfaces.get(surfaceNid) || {}

  if (!typeNid || !categoryNid || !collectionNid) {
    return {}
  }

  const surfaceType = surfaceTypes.get(typeNid)
  const surfaceCategory = surfaceType?.surfaceCategories.get(categoryNid)
  const surfaceCollection =
    surfaceCategory?.surfaceCollections.get(collectionNid)
  const surface = surfaceCollection?.surfaces.get(surfaceNid)
  if (!surface) {
    return {}
  }

  return {
    type: surfaceType,
    category: surfaceCategory,
    collection: surfaceCollection,
    surface: surface,
  }
}

function convertAccessoryToValue(
  accessory: Configuration['accessoriesOptions'][number]['values'][number],
  option: Configuration['accessoriesOptions'][number],
  currentValues: Configuration['values'],
  surfaceTypes: Map<number, SurfaceTypesMapValue>,
  surfaceMaps: SurfaceMaps,
  surfaceOptionsGroups: Map<string, SurfaceOptionsGroup>
) {
  let surfaceOption: Configuration['surfacesOptions'][number] | undefined
  const assignFabricTitle = option.assignFabricTitle
    ? option.multiselectable
      ? `${option.assignFabricTitle} ${accessory.title}`
      : option.assignFabricTitle
    : option.title

  if (accessory.hasSurfacesOption) {
    surfaceOption = {
      partCategoryNid: option.partCategoryNid,
      partCategoryTranslationOfNid: option.partCategoryTranslationOfNid,
      accessoryNid: accessory.accessoryNid,
      accessoryTranslationOfNid: accessory.accessoryTranslationOfNid,
      title: option.title,
      assignFabricTitle: assignFabricTitle,
      emptyOptionTitle: '',
      hasImpactOnPrice: 0,
      optional: 0,
      surfaceCollectionNids: accessory.surfaceCollectionNids,
      surfacesInheritedFromProduct: accessory.surfacesInheritedFromProduct,
      weight: 0,
    }
  }

  const accessoryValue: Configuration['values'][number] = {
    partCategoryNid: option.partCategoryNid,
    partCategoryTranslationOfNid: option.partCategoryTranslationOfNid,
    partNid: accessory.accessoryNid,
    partTranslationOfNid: accessory.accessoryTranslationOfNid,
    partPrice: accessory.price,
    partTitle: accessory.title,
    partImageSrc: accessory.thumbnail,
    isEmpty: accessory.isEmpty,
    basePartDeselected: false,
  }

  if (surfaceOption) {
    let surface:
      | Configuration['surfaceTypes'][number]['surfaceCategories'][number]['surfaceCollections'][number]['surfaces'][number]
      | undefined

    const previousAccessory = findValue(
      currentValues,
      option.partCategoryNid,
      accessory.accessoryNid
    )
    if (previousAccessory && previousAccessory.surfaceNid) {
      // try to carry over surface from previous accessory
      const { typeNid, categoryNid, collectionNid } =
        surfaceMaps.surfaces.get(previousAccessory.surfaceNid) || {}
      if (typeNid && categoryNid && collectionNid) {
        surface = surfaceTypes
          .get(typeNid)
          ?.surfaceCategories.get(categoryNid)
          ?.surfaceCollections.get(collectionNid)
          ?.surfaces.get(previousAccessory.surfaceNid)
      }
    }

    const surfaceCollectionNid = accessory.surfaceCollectionNids[0]
    if (surfaceCollectionNid) {
      const { typeNid, categoryNid } =
        surfaceMaps.surfaceCollections.get(surfaceCollectionNid) || {}

      if (!surface && accessory.surfacesInheritedFromProduct && typeNid) {
        // accessory inherits surface collections from product (these are grouped by surface type)
        // find first used surface in the group
        const type = surfaceTypes.get(typeNid)
        if (type && type.icon?.fileName) {
          const firstOptionInGroup = surfaceOptionsGroups
            .get(type.icon?.fileName)
            ?.surfaceOptionGroups.get(type.nid)
            ?.surfaceOptions.at(0)
          if (firstOptionInGroup) {
            const valueForOption = findValue(
              currentValues,
              firstOptionInGroup.partCategoryNid,
              accessory.accessoryNid
            )
            if (valueForOption && valueForOption.surfaceNid) {
              surface = findSurfaceByNid(
                surfaceTypes,
                surfaceMaps,
                valueForOption.surfaceNid
              ).surface
            }
          }
        }
      }

      if (!surface && typeNid && categoryNid) {
        // as fallback, find first surface in first collection of the accessory
        surface = surfaceTypes
          .get(typeNid)
          ?.surfaceCategories.get(categoryNid)
          ?.surfaceCollections.get(surfaceCollectionNid)
          ?.surfaces.values()
          .next().value
      }
    }

    if (surface) {
      accessoryValue.surfaceNid = surface.nid
      accessoryValue.surfaceTranslationOfNid = surface.translationOfNid
      accessoryValue.surfaceTitle = surface.title
      accessoryValue.surfaceImageSrc = surface.image?.url
    }
  }

  return { surfaceOption, accessoryValue }
}

function isMultiselectableAccessory(
  value: Configuration['values'][number],
  accessoriesOptions: Configuration['accessoriesOptions']
) {
  return !!accessoriesOptions.find(
    (option) => option.partCategoryNid === value.partCategoryNid
  )?.multiselectable
}

function findValue(
  currentValues: Configuration['values'],
  partCategoryNid: number,
  accessoryNid?: number | null
) {
  const values = currentValues.filter(
    (value) => value.partCategoryNid === partCategoryNid
  )
  if (values.length === 1) {
    // non-multiselectable value or there is one multiselectable value selected
    return values[0]
  } else {
    // find correct multiselectable value
    return values.find((value) => value.partNid === accessoryNid)
  }
}

function isAccessoryOption(
  option: any
): option is Configuration['accessoriesOptions'][number] {
  // hacky way to tell if option is accessories option - if multiselectable property changes this will break
  return !!('multiselectable' in option)
}

function getOptionalBasePartTitle(optionalBasePart: {
  surfaceOption: Configuration['surfacesOptions'][number]
  basePartDeselected: boolean
}) {
  if (
    optionalBasePart.basePartDeselected &&
    optionalBasePart.surfaceOption.emptyOptionTitle
  ) {
    return optionalBasePart.surfaceOption.emptyOptionTitle
  } else {
    return optionalBasePart.surfaceOption.title
  }
}

function isAccessoryPicked(
  accessoriesOption: Configuration['accessoriesOptions'][number],
  accessory: Configuration['accessoriesOptions'][number]['values'][number],
  values: Record<string, Configuration['values'][number]>,
  multiselectableAccessories: Record<string, Configuration['values']>
) {
  if (accessoriesOption.multiselectable) {
    return (
      (
        multiselectableAccessories[accessoriesOption.partCategoryNid] || []
      ).findIndex((value) => value.partNid === accessory.accessoryNid) !== -1
    )
  } else {
    return (
      accessory.accessoryNid ===
      values[accessoriesOption.partCategoryNid]?.partNid
    )
  }
}

export const configuratorUtils = {
  getFormattedPrice,
  findAccessory,
  findSurfaceCategoryByNid,
  findSurfaceCollectionByNid,
  findSurfaceByNid,
  convertAccessoryToValue,
  isMultiselectableAccessory,
  findValue,
  isAccessoryOption,
  getOptionalBasePartTitle,
  isAccessoryPicked,
}
